/**
 * Compresses an image file to a target size using Canvas API.
 *
 * @param file - The image file to compress
 * @param maxSizeKB - Target maximum file size in kilobytes (default: 500KB)
 * @param quality - Initial JPEG quality (0-1, default: 0.85)
 * @returns Promise resolving to compressed image blob
 *
 * @example
 * ```tsx
 * const compressed = await compressImage(largeImageFile, 500, 0.85);
 * // Returns blob approximately 500KB or smaller
 * ```
 */
const MIN_QUALITY = 0.3;

export async function compressImage(file: File, maxSizeKB: number = 500, quality: number = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const maxDimension = 1200;
      let { width, height } = image;

      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(image, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            if (blob.size > maxSizeKB * 1024 && quality > MIN_QUALITY) {
              compressImage(new File([blob], file.name, { type: "image/jpeg" }), maxSizeKB, quality * 0.9)
                .then(resolve)
                .catch(reject);
            } else {
              resolve(blob);
            }
          } else {
            reject(new Error("Failed to create compressed image"));
          }
        },
        "image/jpeg",
        quality
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for compression"));
    };

    image.src = objectUrl;
  });
}
